import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import BankSelector from './BankSelector';

const PaymentBreakdown = ({ totalAmount, paymentModes, onChange, label = "Payment Breakdown" }) => {
  const [modes, setModes] = useState(paymentModes || []);
  const [newMode, setNewMode] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newTransactionNumber, setNewTransactionNumber] = useState('');
  const [newBankId, setNewBankId] = useState('');

  useEffect(() => {
    if (paymentModes) {
      setModes(paymentModes);
    }
  }, [paymentModes]);

  const calculateTotal = () => {
    return modes.reduce((sum, mode) => sum + (parseFloat(mode.amount) || 0), 0);
  };

  const getTransactionLabel = (mode) => {
    switch(mode) {
      case 'Card': return 'Transaction Number';
      case 'UPI': return 'UPI Transaction ID';
      case 'Net Banking': return 'Transaction Ref No';
      case 'Cheque': return 'Cheque Number';
      case 'DD': return 'DD Number';
      default: return null;
    }
  };

  const needsTransactionNumber = (mode) => {
    return mode !== 'Cash';
  };

  const handleAddMode = () => {
    if (!newMode || !newAmount || parseFloat(newAmount) <= 0) {
      alert('Please select a payment mode and enter a valid amount');
      return;
    }

    // Validate bank selection for non-cash payments
    if (newMode !== 'Cash' && !newBankId) {
      alert('Please select a bank for non-cash payment');
      return;
    }

    // Validate transaction number for non-cash payments
    if (needsTransactionNumber(newMode) && !newTransactionNumber.trim()) {
      alert(`Please enter ${getTransactionLabel(newMode)} for ${newMode} payment`);
      return;
    }

    const currentTotal = calculateTotal();
    const amountToAdd = parseFloat(newAmount);

    if (currentTotal + amountToAdd > totalAmount) {
      alert(`Total payment (₹${(currentTotal + amountToAdd).toLocaleString('en-IN')}) cannot exceed the required amount (₹${totalAmount.toLocaleString('en-IN')})`);
      return;
    }

    const updatedModes = [...modes, { 
      mode: newMode, 
      amount: amountToAdd,
      transaction_number: newTransactionNumber.trim() || null,
      bank_id: newMode !== 'Cash' ? newBankId : null
    }];
    setModes(updatedModes);
    onChange(updatedModes);
    setNewMode('');
    setNewAmount('');
    setNewTransactionNumber('');
    setNewBankId('');
  };

  const handleRemoveMode = (index) => {
    const updatedModes = modes.filter((_, i) => i !== index);
    setModes(updatedModes);
    onChange(updatedModes);
  };

  const currentTotal = calculateTotal();
  const remaining = totalAmount - currentTotal;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <div className="text-sm">
          <span className="text-gray-600">Total: ₹{totalAmount.toLocaleString('en-IN')}</span>
          {remaining > 0 && (
            <span className="ml-3 text-orange-600 font-medium">
              Remaining: ₹{remaining.toLocaleString('en-IN')}
            </span>
          )}
          {remaining === 0 && modes.length > 0 && (
            <span className="ml-3 text-green-600 font-medium">✓ Fully Allocated</span>
          )}
        </div>
      </div>

      {/* Existing Payment Modes */}
      {modes.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          {modes.map((mode, index) => (
            <div key={index} className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex flex-col gap-1 flex-1">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800">
                    {mode.mode}
                  </span>
                  <span className="text-lg font-semibold text-gray-900">
                    ₹{(parseFloat(mode.amount) || 0).toLocaleString('en-IN')}
                  </span>
                </div>
                {mode.transaction_number && (
                  <div className="text-sm text-gray-600 ml-1">
                    <span className="font-medium">{getTransactionLabel(mode.mode)}:</span> {mode.transaction_number}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleRemoveMode(index)}
                className="text-red-600 hover:text-red-800 p-1 ml-2"
                title="Remove"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add New Payment Mode */}
      {remaining > 0 && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 space-y-3">
          <p className="text-sm font-medium text-gray-700">Add Payment Mode</p>
          <div className="grid grid-cols-1 gap-3">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <select
                value={newMode}
                onChange={(e) => {
                  setNewMode(e.target.value);
                  setNewTransactionNumber('');
                  setNewBankId('');
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Mode</option>
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Card">Card</option>
                <option value="Net Banking">Net Banking</option>
                <option value="Cheque">Cheque</option>
                <option value="DD">Demand Draft</option>
              </select>

              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-400">₹</span>
                <input
                  type="number"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="0"
                  max={remaining}
                  step="0.01"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {newMode && needsTransactionNumber(newMode) && (
                <input
                  type="text"
                  value={newTransactionNumber}
                  onChange={(e) => setNewTransactionNumber(e.target.value)}
                  placeholder={getTransactionLabel(newMode)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            </div>

            <BankSelector 
              paymentMode={newMode}
              selectedBank={newBankId}
              onBankChange={setNewBankId}
              required={true}
            />

            <button
              type="button"
              onClick={handleAddMode}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full md:w-auto"
            >
              <Plus size={18} />
              Add Payment Mode
            </button>
          </div>
          {newMode && needsTransactionNumber(newMode) && (
            <p className="text-xs text-gray-500">
              * {getTransactionLabel(newMode)} is required for {newMode} payments
            </p>
          )}
        </div>
      )}

      {/* Validation Message */}
      {modes.length === 0 && (
        <p className="text-sm text-orange-600">
          ⚠️ Please add at least one payment mode to continue
        </p>
      )}
      {remaining > 0 && modes.length > 0 && (
        <p className="text-sm text-orange-600">
          ⚠️ Please allocate the remaining ₹{remaining.toLocaleString('en-IN')}
        </p>
      )}
    </div>
  );
};

export default PaymentBreakdown;
